import useCurrentSession from "@features/session/hooks/useCurrentSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { useCallback, useEffect, useRef } from "react";
import { deriveItemTimingPlan } from "../../helpers/item-timing-plan";
import useResponseControlsStore from "../../hooks/useResponseControlsStore";

type Params = {
	handleNext: (options?: { skipCompleteTestDialog?: boolean }) => void;
	onTimeExpired: () => void;
};

const useAutoSkipOnTimeout = ({ handleNext, onTimeExpired }: Params) => {
	const sessionId = useSessionStore((s) => s.id);
	const isSummativeTest = useSessionStore((s) => s.isSummativeTest);
	const inReviewMode = useSessionStore((s) => s.inReviewMode);
	const { currentItem } = useCurrentSession();
	const currentItemId = currentItem?.id!;
	const completedResponseItems = useResponseControlsStore(
		(s) => s.completedItems,
	);
	const markResponseComplete = useResponseControlsStore(
		(s) => s.markResponseComplete,
	);
	const completeActiveResponse = useResponseControlsStore(
		(s) => s.completeActiveResponse,
	);

	const autoSkippedRef = useRef<Record<string, boolean>>({});
	const handleNextRef = useRef(handleNext);
	handleNextRef.current = handleNext;
	const onTimeExpiredRef = useRef(onTimeExpired);
	onTimeExpiredRef.current = onTimeExpired;

	const timingPlan = deriveItemTimingPlan({
		item: currentItem,
		isSummativeTest,
		inReviewMode,
	});
	const {
		timesOutWhenExpired,
		timeLimitSeconds,
		timerWaitsForAudioCompletion,
		itemUiDrivesOwnTimer,
		prepSecondsBeforeTimer,
		autoSkipsOnTimeout,
	} = timingPlan;

	const isCurrentResponseComplete = Boolean(
		completedResponseItems[currentItemId],
	);
	const isItemAudioComplete = useResponseControlsStore((s) =>
		Boolean(s.audioCompletedItems[currentItemId]),
	);

	// Persisted elapsed (prep counted once fully consumed) shortens the arm on
	// resume so expiry still fires at the item's true remaining time.
	const storedItemTimeElapsed = currentItem?.answer?.timeElapsed ?? 0;
	const totalTimeBeforeExpirySeconds = timerWaitsForAudioCompletion
		? (timeLimitSeconds ?? 0)
		: prepSecondsBeforeTimer + (timeLimitSeconds ?? 0);
	const autoSkipDelaySeconds = Math.max(
		0,
		totalTimeBeforeExpirySeconds - storedItemTimeElapsed,
	);

	const expiredBeforeResume =
		totalTimeBeforeExpirySeconds > 0 &&
		storedItemTimeElapsed >= totalTimeBeforeExpirySeconds;
	const autoSkipAwaitingAudio =
		timerWaitsForAudioCompletion &&
		!isItemAudioComplete &&
		!expiredBeforeResume;

	useEffect(() => {
		if (
			!timesOutWhenExpired ||
			isCurrentResponseComplete ||
			itemUiDrivesOwnTimer ||
			autoSkipAwaitingAudio ||
			typeof timeLimitSeconds !== "number"
		) {
			return;
		}
		const timeout = setTimeout(
			() => markResponseComplete(currentItemId),
			autoSkipDelaySeconds * 1000,
		);
		return () => clearTimeout(timeout);
	}, [
		timesOutWhenExpired,
		isCurrentResponseComplete,
		itemUiDrivesOwnTimer,
		autoSkipAwaitingAudio,
		timeLimitSeconds,
		autoSkipDelaySeconds,
		markResponseComplete,
		currentItemId,
	]);

	// autoSkipsOnTimeout true → advance silently (handleNext submits the answer
	// event with whatever the user entered); false → time-expired dialog.
	useEffect(() => {
		if (
			!(timesOutWhenExpired && isCurrentResponseComplete) ||
			autoSkippedRef.current[currentItemId]
		) {
			return;
		}
		autoSkippedRef.current[currentItemId] = true;
		if (autoSkipsOnTimeout) {
			handleNextRef.current({ skipCompleteTestDialog: true });
			return;
		}
		onTimeExpiredRef.current();
	}, [
		timesOutWhenExpired,
		isCurrentResponseComplete,
		autoSkipsOnTimeout,
		currentItemId,
	]);

	const handleNextAfterResponseCompletion = useCallback(async () => {
		autoSkippedRef.current[currentItemId] = true;
		await completeActiveResponse(currentItemId);
		handleNext();
	}, [completeActiveResponse, handleNext, currentItemId]);

	const advanceAfterTimeExpired = useCallback(() => {
		handleNextRef.current({ skipCompleteTestDialog: true });
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		autoSkippedRef.current = {};
	}, [sessionId]);

	return {
		currentResponseTimeLimit: timeLimitSeconds,
		handleNextAfterResponseCompletion,
		advanceAfterTimeExpired,
	};
};

export default useAutoSkipOnTimeout;
